import { ChangeDetectorRef, Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { CimClass, Connectivity, ConnectivityDto, EquipmentStatus, EquipmentTemplateDto, PropertyType, SubstationTemplateDto } from '../../../../models/network-structure.model';
import { Voltage } from '../../../../enums/voltage';
import { ToastrService } from 'ngx-toastr';
import { EquipmentTemplateService } from '../../../../services/equipment-template.service';
import { UtilitySharedService } from '../../../../services/shared/utility-shared.service';
import { NodeType } from '../../../../enums/node-type';
import { SectionDefinitionService } from '../../../../services/section-definition.service';
import { SectionDefinitionEntityType } from '../../../../models/section-definition.model';
import { PermissionsService } from '../../../../services/permissions.service';

@Component({
  selector: 'lib-common-equipment-template',
  templateUrl: './common-equipment-template.component.html',
  styleUrls: ['./common-equipment-template.component.scss'],
  standalone: false
})
export class CommonEquipmentTemplateComponent {
  @Input() equipment!: EquipmentTemplateDto;
  @Input() substation!: SubstationTemplateDto;
  @Input() editEnabled: boolean = false;
  @Input() deleteClicked!: boolean;
  @Output() equipmentChanged = new EventEmitter<void>();
  @Output() saveClicked = new EventEmitter<EquipmentTemplateDto>();

  Voltage = Voltage;
  Connectivity = Connectivity;
  NodeType = NodeType;
  PropertyType = PropertyType;

  connectivitySourceOptions: EquipmentTemplateDto[] = [];
  connectivityDestinationOptions: EquipmentTemplateDto[] = [];

  connectivitySourceInitialValue: EquipmentTemplateDto[] = [];
  connectivityDestinationInitialValue: EquipmentTemplateDto[] = [];

  utilityId: number;

  constructor(
    private toastr: ToastrService, 
    private eqTemplateService: EquipmentTemplateService, 
    private utilitySharedService: UtilitySharedService,
    public permissionsService: PermissionsService
  ) {
    this.utilityId = Number(this.utilitySharedService.getUtilityId());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['equipment']) {
      this.getConnectivityObjects();
      this.updateFilteredLists();
      
      this.editEnabled = this.equipment.isNew;
    }

    if (changes['deleteClicked'] && !changes['deleteClicked'].firstChange) {
      if (this.equipment.id) {
        const obj = {
          id: this.equipment.id,
          utilityId: this.utilityId
        }
        this.eqTemplateService.delete(obj).subscribe({
          next: (response: any) => {
            if (response?.body?.status == 200 || response?.body?.status == 'OK') {
              this.toastr.success(response?.body?.message);        
              setTimeout(() => {      
                window.location.reload();
              }, 1000);
            }
            else {
              this.toastr.error(response?.body?.message);
            }
          }
        });
      } else {
        this.toastr.info("You can't delete this equipment, because it's not saved.");
      }
    }
  }

  private updateFilteredLists() {
    const allOptions = this.substation.equipmentTemplates.filter(
      eq => eq.guid !== this.equipment.guid
    );
    
    this.connectivitySourceOptions = allOptions.filter(
      eq => !this.connectivityDestinationInitialValue.some(dest => dest.guid === eq.guid)
    );
    
    this.connectivityDestinationOptions = allOptions.filter(
      eq => !this.connectivitySourceInitialValue.some(src => src.guid === eq.guid)
    );
  }

  private getConnectivityObjects() {
    const sourceConnectivities = this.equipment.connectivities?.filter(
      c => c.nodeType?.toString() == 'Source'
    ) || [];

    this.connectivitySourceInitialValue = this.substation.equipmentTemplates.filter(
      eq => sourceConnectivities.some(c => c.guid === eq.guid)
    );
    
    const destConnectivities = this.equipment.connectivities?.filter(
      c => c.nodeType?.toString() == 'Destination'
    ) || [];

    this.connectivityDestinationInitialValue = this.substation.equipmentTemplates.filter(
      eq => destConnectivities.some(c => c.guid === eq.guid)
    );
  }

  private getConnectivityByNodeType(nodeType: NodeType): ConnectivityDto | null {
    if (!this.equipment.connectivities || this.equipment.connectivities.length === 0) {
      return null;
    }
    
    return this.equipment.connectivities.find(c => c.nodeType === nodeType) || null;
  }

  private ensureConnectivitiesArray() {
    if (!this.equipment.connectivities) {
      this.equipment.connectivities = [];
    }
  }
  
  onSelectVoltage(obj: Voltage) {
    this.equipment.voltage = obj;
  }
  
  onSelectConnectivitySource(selectedEquipments: EquipmentTemplateDto[]) {
    this.ensureConnectivitiesArray();

    // 1. Filtriramo sve što NIJE Source. 
    // Proveravamo i string i enum varijantu za svaki slučaj zbog backend-a.
    const nonSourceConnectivities = this.equipment.connectivities.filter(c => 
      c.nodeType !== NodeType.Source && 
      c.nodeType?.toString() !== 'Source'
    );

    // 2. Mapiramo nove Source objekte
    const newSources = (selectedEquipments || []).map(eq => {
      const newConn = new ConnectivityDto();
      newConn.guid = eq.guid;
      newConn.utilityId = this.utilityId;
      newConn.nodeType = NodeType.Source;
      newConn.equipmentTemplateId = eq.id;
      return newConn;
    });

    // 3. Spajamo u novi niz (Immutable update)
    this.equipment.connectivities = [...nonSourceConnectivities, ...newSources];
    
    // 4. Ažuriramo initialValue (da dropdown zna šta je selektovano)
    this.connectivitySourceInitialValue = [...selectedEquipments];

    // 5. Osvežavamo opcije za Destination (da se ne može izabrati ista oprema na obe strane)
    const allPossibleOptions = this.substation.equipmentTemplates.filter(
      eq => eq.guid !== this.equipment.guid
    );
    
    this.connectivityDestinationOptions = allPossibleOptions.filter(
      eq => !selectedEquipments.some(src => src.guid === eq.guid)
    );

    this.notifySchematicDiagram();
  }

  onSelectConnectivityDestination(selectedEquipments: EquipmentTemplateDto[]) {
    this.ensureConnectivitiesArray();

    // 1. Filtriramo sve što NIJE Destination
    const nonDestConnectivities = this.equipment.connectivities.filter(c => 
      c.nodeType !== NodeType.Destination && 
      c.nodeType?.toString() !== 'Destination'
    );

    // 2. Mapiramo nove Destination objekte
    const newDests = (selectedEquipments || []).map(eq => {
      const newConn = new ConnectivityDto();
      newConn.guid = eq.guid;
      newConn.utilityId = this.utilityId;
      newConn.nodeType = NodeType.Destination;
      newConn.equipmentTemplateId = eq.id;
      return newConn;
    });

    // 3. Spajamo u novi niz
    this.equipment.connectivities = [...nonDestConnectivities, ...newDests];

    // 4. Ažuriramo initialValue
    this.connectivityDestinationInitialValue = [...selectedEquipments];

    // 5. Osvežavamo opcije za Source
    const allPossibleOptions = this.substation.equipmentTemplates.filter(
      eq => eq.guid !== this.equipment.guid
    );

    this.connectivitySourceOptions = allPossibleOptions.filter(
      eq => !selectedEquipments.some(dest => dest.guid === eq.guid)
    );

    this.notifySchematicDiagram();
  }
  
  onSelectConnectivitySourceVoltage(obj: Voltage) {
    const sourceConnectivity = this.getConnectivityByNodeType(NodeType.Source);
    if (sourceConnectivity) {
      sourceConnectivity.voltage = obj;
    }
  }
  
  onSelectConnectivityDestinationVoltage(obj: Voltage) {
    const destConnectivity = this.getConnectivityByNodeType(NodeType.Destination);
    if (destConnectivity) {
      destConnectivity.voltage = obj;
    }
  }

  private save(obj: EquipmentTemplateDto) {
    this.saveClicked.emit(obj);
    this.toastr.success("Equipment saved successfully.");
  }

  onSave() {
    if (this.equipment.className == '') {
      this.toastr.warning("Class Name field is mandatory.");
      return;
    }
    else if (!/^[\p{L}\s]+$/u.test(this.equipment.className.trim())) {
      this.toastr.warning("Class Name field must contain only letters.");
      return;
    }
    else if (this.equipment.voltage == null) {
      this.toastr.warning("Voltage field is mandatory.");
      return;
    }
    else if (this.equipment.visualizationConfiguration.symbol == null) {
      this.toastr.warning("Symbol is mandatory.");
      return;
    }
    else {
      this.equipment.status = EquipmentStatus.Complete;
      this.save(this.equipment);
    }
  }

  notifySchematicDiagram() {
    this.equipmentChanged.emit();
  }
}